import { useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { isValidTag } from "@/lib/isValidTag";

interface TagChipsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  placeholder?: string;
  maxChips?: number;
}

const MAX_VISIBLE_SUGGESTIONS = 20;

export function TagChipsInput({
  value,
  onChange,
  suggestions,
  placeholder = "Tag hinzufügen...",
  maxChips,
}: TagChipsInputProps) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);

  const filteredSuggestions = useMemo(() => {
    const lower = input.toLowerCase();
    return suggestions
      .filter((s) => !value.includes(s) && s.includes(lower))
      .slice(0, MAX_VISIBLE_SUGGESTIONS);
  }, [input, suggestions, value]);

  const atMax =
    maxChips !== undefined && value.length >= maxChips;
  const isInputValid = isValidTag(input);
  const canAddCustom =
    !atMax &&
    input.length > 0 &&
    isInputValid &&
    !value.includes(input) &&
    !suggestions.includes(input);

  function addTag(tag: string) {
    if (!isValidTag(tag)) return;
    if (atMax) return;
    if (value.includes(tag)) return;
    onChange([...value, tag]);
    setInput("");
  }

  function removeTag(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map((tag) => (
        <TagChip key={tag} tag={tag} onRemove={() => removeTag(tag)} />
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <input
            type="text"
            value={input}
            disabled={atMax}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (canAddCustom) {
                  e.preventDefault();
                  addTag(input);
                  setOpen(false);
                } else if (input.length > 0 && !isInputValid) {
                  e.preventDefault();
                }
              }
            }}
            placeholder={atMax ? "Maximum erreicht" : placeholder}
            aria-label="Tag-Eingabe"
            className={cn(
              "h-8 min-w-[140px] rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring",
              input && !isInputValid && "border-red-400",
              atMax && "cursor-not-allowed opacity-50",
            )}
          />
        </PopoverTrigger>
        <PopoverContent
          className="w-[240px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList>
              {filteredSuggestions.length === 0 && !canAddCustom && (
                <CommandEmpty>Keine passenden Tags</CommandEmpty>
              )}
              {filteredSuggestions.length > 0 && (
                <CommandGroup heading="Vorschläge">
                  {filteredSuggestions.map((sug) => (
                    <CommandItem
                      key={sug}
                      value={sug}
                      onSelect={() => {
                        addTag(sug);
                        setOpen(false);
                      }}
                    >
                      {sug}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {canAddCustom && (
                <CommandGroup heading="Neu">
                  <CommandItem
                    value={`__new__${input}`}
                    onSelect={() => {
                      addTag(input);
                      setOpen(false);
                    }}
                  >
                    „{input}&ldquo; als neuen Tag anlegen
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TagChip({
  tag,
  onRemove,
}: {
  tag: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
      {tag}
      <button
        type="button"
        onClick={onRemove}
        className="text-muted-foreground hover:text-destructive"
        aria-label={`Tag ${tag} entfernen`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
